import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserService } from '../../services/user.service';
import { DomainStateService } from './domain-state.service';

@Injectable({ providedIn: 'root' })
export class DomainInitService {
  constructor(
    private readonly userService: UserService,
    private readonly domainStateService: DomainStateService
  ) {}

  async init(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) return;

      const email = session.tokens.idToken.payload['email'] as string | undefined;
      if (email) this.domainStateService.setUserContext(email);

      // Same endpoint dashboard-home uses: /data/user/me/domains
      const response = await this.userService.getUserDomains('', 1, 1000, '');
      if (!response?.domains?.length) return;

      const domains = response.domains;
      const isSuperAdmin = domains.some((d: any) => d.role?.code === 'superadmin');
      const isAtLeastAdmin = domains.some((d: any) =>
        d.role?.code === 'superadmin' || d.role?.code === 'admin'
      );

      this.domainStateService.setIsSuperAdmin(isSuperAdmin);
      this.domainStateService.setIsAtLeastAdmin(isAtLeastAdmin);

      const stored = this.domainStateService.getStoredDomain();
      const firstPrefix = domains[0]?.domain?.prefix;
      const ALL = DomainStateService.ALL_CUSTOMERS_SENTINEL;

      if (isSuperAdmin) {
        // null   → first login after logout: land on first domain in list
        // '__ALL__' → was in "All customers" mode: restore it (no impersonation)
        // other  → was on a specific domain: restore it with impersonation
        let targetPrefix: string;
        if (stored === null) {
          targetPrefix = firstPrefix ?? '';
        } else if (stored === ALL) {
          targetPrefix = '';
        } else {
          targetPrefix = stored;
        }
        this.domainStateService.setDomain(targetPrefix);
        if (targetPrefix) {
          const targetData = domains.find((d: any) => d.domain?.prefix === targetPrefix);
          const roleCode = targetData?.role?.code ?? '';
          const isAdmin = roleCode === 'admin' || roleCode === 'superadmin' || isAtLeastAdmin;
          this.domainStateService.startImpersonation(isAdmin);
        }
      } else {
        // Non-super-admin: restore stored domain (never '__ALL__') or fall back to first.
        const effective = (stored && stored !== ALL) ? stored : (firstPrefix ?? '');
        this.domainStateService.setDomain(effective);
      }
    } catch {
      // Silent fail — try localStorage as last resort
      const stored = this.domainStateService.getStoredDomain();
      if (stored) {
        this.domainStateService.setDomain(stored);
      }
    }
  }
}
